import React, { useState } from 'react';

import { useFormik } from 'formik';
import * as yup from 'yup';

import Modal from 'components/Modal/Modal';
import RoundedButton from 'components/RoundedButton/RoundedButton';

import styles from '../SubscriptionPackages.module.scss';

const formatLabel = (value) =>
  String(value || '')
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const countryLabels = {
  '*': 'All countries',
  ARE: 'United Arab Emirates',
  KSA: 'Saudi Arabia',
  UAE: 'United Arab Emirates',
};

const formatCountryLabel = (country) => countryLabels[country] || formatLabel(country);

const sortCountryConnections = (connections) =>
  [...connections].sort((left, right) => {
    if (left.country === '*') return 1;
    if (right.country === '*') return -1;
    return formatCountryLabel(left.country).localeCompare(formatCountryLabel(right.country));
  });

const buildValidationSchema = (countries) =>
  yup.object().shape({
    identifier: yup
      .string()
      .trim()
      .matches(/^[a-z0-9][a-z0-9-]*$/, 'Use lowercase letters, numbers and hyphens')
      .required('Identifier is required'),
    name: yup.string().trim().required('Name is required'),
    description: yup.string().trim().required('Description is required'),
    platformName: yup.string().trim().required('Platform name is required'),
    platformDescription: yup.string().trim().required('Platform description is required'),
    country: yup.string().oneOf(countries, 'Select a configured country').required('Country is required'),
    pricingModel: yup.string().required('Pricing model is required'),
    defaultUnitPrice: yup
      .number()
      .typeError('Default unit price must be a number')
      .moreThan(0, 'Default unit price must be greater than 0')
      .required('Default unit price is required'),
    currency: yup
      .string()
      .trim()
      .matches(/^[A-Z]{3}$/, 'Use a 3-letter uppercase currency code')
      .required('Currency is required'),
    defaultBillingPeriod: yup
      .string()
      .required('Billing period is required')
      .test(
        'one-time-not-applicable',
        'One-time packages cannot use NOT_APPLICABLE billing period',
        function test(value) {
          return !(this.parent.pricingModel === 'ONE_TIME' && value === 'NOT_APPLICABLE');
        }
      ),
    pricingUnit: yup.string().required('Pricing unit is required'),
    isPrimary: yup.boolean(),
    isSingleCharge: yup.boolean(),
    isService: yup.boolean(),
    includeInPlg: yup.boolean(),
  });

const getOptionValue = (option) => (typeof option === 'string' ? option : option.value);

const getOptionLabel = (option) => (typeof option === 'string' ? formatLabel(option) : option.label);

const SelectField = ({ formik, name, label, options }) => (
  <label className={styles.field}>
    <span>{label}</span>
    <select
      aria-label={label}
      name={name}
      value={formik.values[name]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
    >
      <option value=''>Select</option>
      {options.map((option) => (
        <option key={getOptionValue(option)} value={getOptionValue(option)}>
          {getOptionLabel(option)}
        </option>
      ))}
    </select>
    {(formik.touched[name] || formik.submitCount > 0) && formik.errors[name] && (
      <small className={styles.error}>{formik.errors[name]}</small>
    )}
  </label>
);

const TextField = ({ formik, name, label, type = 'text', placeholder, hint }) => (
  <label className={styles.field}>
    <span>{label}</span>
    <input
      aria-label={label}
      name={name}
      type={type}
      placeholder={placeholder}
      value={formik.values[name]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
    />
    {hint && <small className={styles.fieldHint}>{hint}</small>}
    {(formik.touched[name] || formik.submitCount > 0) && formik.errors[name] && (
      <small className={styles.error}>{formik.errors[name]}</small>
    )}
  </label>
);

const ToggleField = ({ formik, name, label, description }) => (
  <label className={styles.toggleField}>
    <input
      name={name}
      type='checkbox'
      checked={formik.values[name]}
      onChange={(event) => formik.setFieldValue(name, event.target.checked)}
    />
    <span>
      <strong>{label}</strong>
      {description && <small>{description}</small>}
    </span>
  </label>
);

const CreateSubscriptionPackageModal = ({ isOpen, meta, onClose, onSubmit, isSubmitting }) => {
  const [step, setStep] = useState('form');
  const [pendingValues, setPendingValues] = useState(null);
  const chargebeeConnections = meta?.chargebeeConnections || [];
  const sortedChargebeeConnections = sortCountryConnections(chargebeeConnections);
  const countries = chargebeeConnections.map((connection) => connection.country);
  const countryOptions = sortedChargebeeConnections.map((connection) => ({
    value: connection.country,
    label: formatCountryLabel(connection.country),
  }));
  const pricingModels = meta?.pricingModels || [];
  const pricingUnits = meta?.pricingUnits || [];
  const billingPeriods = meta?.billingPeriods || [];

  const initialValues = {
    identifier: '',
    name: '',
    description: '',
    platformName: '',
    platformDescription: '',
    country: '',
    pricingModel: pricingModels[0] || '',
    defaultUnitPrice: '',
    currency: '',
    defaultBillingPeriod: billingPeriods[0] || '',
    pricingUnit: pricingUnits[0] || '',
    isPrimary: false,
    isSingleCharge: false,
    isService: false,
    includeInPlg: false,
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: buildValidationSchema(countries),
    onSubmit: (values) => {
      setPendingValues(values);
      setStep('confirm');
    },
  });

  const closeAndReset = () => {
    formik.resetForm();
    setPendingValues(null);
    setStep('form');
    onClose();
  };

  const handleConfirm = async () => {
    const payload = {
      ...pendingValues,
      defaultUnitPrice: Number(pendingValues.defaultUnitPrice),
    };

    await onSubmit(payload);
    closeAndReset();
  };

  return (
    <Modal
      styles={{ minHeight: 400, width: 760 }}
      title={step === 'form' ? 'Create Subscription Package' : 'Confirm Live Chargebee Sync'}
      isOpen={isOpen}
      onRequestClose={closeAndReset}
      shouldCloseOnOverlayClick={false}
    >
      {step === 'form' ? (
        <form className={styles.createForm} noValidate onSubmit={formik.handleSubmit}>
          <div className={styles.formGrid}>
            <TextField
              formik={formik}
              name='identifier'
              label='Identifier'
              placeholder='starter'
              hint='Example: starter. Use lowercase letters, numbers and hyphens.'
            />
            <TextField formik={formik} name='currency' label='Currency' placeholder='SAR' hint='Example: SAR' />
            <TextField
              formik={formik}
              name='name'
              label='Name'
              placeholder='PACKAGES.PALMHR.STARTER.NAME'
              hint='Example: PACKAGES.PALMHR.STARTER.NAME'
            />
            <TextField
              formik={formik}
              name='description'
              label='Description'
              placeholder='PACKAGES.PALMHR.STARTER.DESCRIPTION'
              hint='Example: PACKAGES.PALMHR.STARTER.DESCRIPTION'
            />
            <TextField
              formik={formik}
              name='platformName'
              label='Billing name'
              placeholder='Starter'
              hint='Example: Starter. This is the name sent to billing.'
            />
            <TextField
              formik={formik}
              name='platformDescription'
              label='Billing description'
              placeholder='Starter subscription package'
              hint='Example: Starter subscription package. This is sent to billing.'
            />
            <SelectField formik={formik} name='country' label='Country' options={countryOptions} />
            <SelectField formik={formik} name='pricingModel' label='Pricing Model' options={pricingModels} />
            <TextField
              formik={formik}
              name='defaultUnitPrice'
              label='Default Unit Price'
              type='number'
              placeholder='1000'
              hint='Example: 1000'
            />
            <SelectField formik={formik} name='defaultBillingPeriod' label='Billing Period' options={billingPeriods} />
            <SelectField formik={formik} name='pricingUnit' label='Pricing Unit' options={pricingUnits} />
          </div>

          <div className={styles.toggleGrid}>
            <ToggleField
              formik={formik}
              name='isPrimary'
              label='Primary package'
              description='Use for base subscriptions. Primary packages appear in the New Account package dropdown.'
            />
            <ToggleField formik={formik} name='isSingleCharge' label='Single charge' />
            <ToggleField formik={formik} name='isService' label='Service package' />
            <ToggleField formik={formik} name='includeInPlg' label='Include in PLG' />
          </div>

          <div className={styles.modalActions}>
            <RoundedButton text='Cancel' type='button' btnStyle='outlined' onClick={closeAndReset} />
            <RoundedButton text='Review' color='primary' type='submit' />
          </div>
        </form>
      ) : (
        <div>
          <div className={styles.confirmBox}>
            <div>
              <strong>{pendingValues.platformName}</strong>
              <span>{pendingValues.identifier}</span>
            </div>
            <div>
              <strong>
                {pendingValues.currency} {pendingValues.defaultUnitPrice}
              </strong>
              <span>
                {formatCountryLabel(pendingValues.country)} / {formatLabel(pendingValues.defaultBillingPeriod)} /{' '}
                {formatLabel(pendingValues.pricingModel)}
              </span>
            </div>
          </div>

          <p className={styles.syncWarning}>
            Creating this package runs live Chargebee sync. The row stays deprecated if Chargebee rejects any variation.
          </p>

          <div className={styles.modalActions}>
            <RoundedButton
              text='Back'
              type='button'
              btnStyle='outlined'
              onClick={() => setStep('form')}
              disabled={isSubmitting}
            />
            <RoundedButton
              text={isSubmitting ? 'Syncing' : 'Create and Sync'}
              color='primary'
              type='button'
              onClick={handleConfirm}
              disabled={isSubmitting}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CreateSubscriptionPackageModal;
